import axios from 'axios';
import { ENV } from 'config/env';
import { API_ENDPOINTS } from 'constants/apiEndpoints';
import { tokenService } from './tokenService';
import { authEvents, AUTH_EVENTS } from './authEvents';
import { logger } from 'utils/logger';

/**
 * Centralized Axios instance.
 *
 * Responsibilities:
 *  - base URL, timeout, JSON defaults
 *  - attach Authorization header (request interceptor)
 *  - transparent access-token refresh on 401 (response interceptor)
 *  - retry transient failures (network / 5xx) up to ENV.API_RETRY_COUNT
 *  - normalize 401/403/500 + network errors into emitted auth events
 *
 * Every request in the app MUST go through this instance (or the httpClient
 * wrapper that uses it). No other axios config should exist.
 */
const axiosInstance = axios.create({
  baseURL: ENV.API_BASE_URL,
  timeout: ENV.API_TIMEOUT,
  headers: { 'Content-Type': 'application/json' },
});

/* --------------------------- Request interceptor --------------------------- */
axiosInstance.interceptors.request.use(
  (config) => {
    const token = tokenService.getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.metadata = { ...(config.metadata || {}), startedAt: Date.now() };
    return config;
  },
  (error) => Promise.reject(error)
);

/* ----------------------- Refresh-token coordination ------------------------ */
// Ensures only ONE refresh request runs at a time; concurrent 401s queue up
// and resume once the new token is available.
let isRefreshing = false;
let pendingQueue = [];

const resolveQueue = (error, token = null) => {
  pendingQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token)));
  pendingQueue = [];
};

const performRefresh = async () => {
  const refreshToken = tokenService.getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');

  // Use a bare axios call so this request does NOT pass through the
  // interceptors (which would recurse on another 401).
  const { data } = await axios.post(
    `${ENV.API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
    { refresh_token: refreshToken },
    { timeout: ENV.API_TIMEOUT }
  );

  const accessToken = data.access_token || data.accessToken;
  const newRefreshToken = data.refresh_token || data.refreshToken;
  tokenService.setTokens({ accessToken, refreshToken: newRefreshToken });
  return accessToken;
};

const shouldRetry = (error) => {
  const status = error.response?.status;
  const isNetwork = !error.response && Boolean(error.request);
  const isServer = status >= 500 && status < 600;
  const isIdempotent = ['get', 'head', 'options'].includes(
    (error.config?.method || 'get').toLowerCase()
  );
  return (isNetwork || isServer) && isIdempotent;
};

const rewriteDriveUrls = (data) => {
  if (typeof data === 'string') {
    const match = data.match(/drive\.google\.com\/uc\?export=view&id=([^&]+)/);
    if (match) {
      return `${ENV.API_BASE_URL}/files/proxy/${match[1]}`;
    }
    const match2 = data.match(/drive\.google\.com\/file\/d\/([^\/]+)\/view/);
    if (match2) {
      return `${ENV.API_BASE_URL}/files/proxy/${match2[1]}`;
    }
    return data;
  }
  if (Array.isArray(data)) {
    return data.map(rewriteDriveUrls);
  }
  if (data !== null && typeof data === 'object') {
    const newData = {};
    for (const key in data) {
      newData[key] = rewriteDriveUrls(data[key]);
    }
    return newData;
  }
  return data;
};

/* -------------------------- Response interceptor --------------------------- */
axiosInstance.interceptors.response.use(
  (response) => {
    if (response.data) {
      response.data = rewriteDriveUrls(response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;

    /* ---- Transparent token refresh on 401 ---- */
    if (status === 401 && !originalRequest._retryAuth) {
      // Don't try to refresh the refresh/login calls themselves.
      const url = originalRequest.url || '';
      const isAuthCall =
        url.includes(API_ENDPOINTS.AUTH.REFRESH) || url.includes(API_ENDPOINTS.AUTH.LOGIN);

      if (!isAuthCall && tokenService.getRefreshToken()) {
        originalRequest._retryAuth = true;

        if (isRefreshing) {
          // Wait for the in-flight refresh to finish, then replay.
          return new Promise((resolve, reject) => {
            pendingQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;
        try {
          const newToken = await performRefresh();
          resolveQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          resolveQueue(refreshError, null);
          tokenService.clearTokens();
          logger.warn('Token refresh failed; signing out.');
          authEvents.emit(AUTH_EVENTS.SESSION_EXPIRED);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // No refresh token available (and not a login/refresh call) -> session is over.
      if (!isAuthCall) {
        tokenService.clearTokens();
        authEvents.emit(AUTH_EVENTS.SESSION_EXPIRED);
      }
      return Promise.reject(error);
    }

    /* ---- Forbidden ---- */
    if (status === 403) {
      const url = originalRequest.url || '';
      if (!url.includes(API_ENDPOINTS.AUTH.LOGIN)) {
        authEvents.emit(AUTH_EVENTS.FORBIDDEN, error);
      }
    }

    /* ---- Retry transient errors ---- */
    if (shouldRetry(error)) {
      originalRequest._retryCount = originalRequest._retryCount || 0;
      if (originalRequest._retryCount < ENV.API_RETRY_COUNT) {
        originalRequest._retryCount += 1;
        const backoff = 300 * 2 ** (originalRequest._retryCount - 1);
        await new Promise((r) => setTimeout(r, backoff));
        return axiosInstance(originalRequest);
      }
    }

    /* ---- Global Server/Network Error Catch ---- */
    if (!status || status >= 500) {
      authEvents.emit(AUTH_EVENTS.SERVER_ERROR, error);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
